import styled from '@emotion/styled'
import { Router } from 'found'
import { TFunction } from 'i18next'
import { Component, ComponentType } from 'react'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { RootState, TypedDispatch } from '../../'
import { fetchContactLocations, fetchFindings, fetchTimetable } from '../../actions/api'
import { setPunctualityParams } from '../../actions/punctuality'
import {
  fetchTrainEquipmentCirculationAction,
  TrainEquipmentCirculationParams,
} from '../../actions/train-equipment-circulation'
import { fetchTrainDataAction } from '../../actions/wagons'
import {
  FETCH_CONTACT_LOCATIONS_POLL_RATE_MS,
  FETCH_FINDINGS_POLL_RATE_MS,
  TASK_TITLE_MAX_CHAR,
} from '../../constants'
import { findMostCritical, taskHasARestriction } from '../../lib/findings'
import moment from '../../lib/moment-fi'
import { isSm3, isSm6 } from '../../lib/smUtils'
import { getName } from '../../lib/stations'
import { generateEquipmnentInfoKeyFromTask } from '../../lib/trainEquipmentUtils'
import { setPreSelectedSerialNumbers } from '../../reducers/createDefectSlice'
import { selectTrainEquipmentCirculationByKey } from '../../reducers/trainEquipmentCirculationSlice'
import { createTrainId } from '../../reducers/wagonSlice'
import {
  commuterFromTrainDetails,
  commuterToTrainDetails,
  filterEquipments,
  findingsSelector,
  inferTaskEquipment,
  inferTaskEquipmentDisplay,
  nowSelector,
  resolveTaskContactReferences,
  selectEquipmentFindings,
  selectOperatingDateForTask,
  shiftTasksToTimetableParams,
  taskIsActive,
} from '../../Selectors'
import { getColor, LAYOUT_LEFT_EDGE, theme } from '../../Theme'
import type {
  Contact,
  FindingsState,
  RollingGuideData,
  Shift,
  Task,
  TaskContactRef,
  TaskDetailsInput,
  TaskEquipment,
} from '../../types'
import type {
  ContactProps,
  TaskContact,
  TimetableParams,
  TrainEquipmentCirculation,
} from '../../types/Input'
import ContactList from '../contact/ContactList'
import { dataWithStatus, Equipment } from '../create-defect/types'
import { DelayLabelRow } from '../DelayLabelRow'
import EnergyEfficiencyTaskInfo from '../energy-efficiency/EnergyEfficiencyTaskInfo'
import RestrictionText from '../findings/RestrictionText'
import TaskButtonRow from './TaskButtonRow'
import TaskDetails from './TaskDetails'
import TaskReport from './TaskObservation'
import TaskTowingRowContainer from './TaskTowingRowContainer'

interface MarginSidesProps {
  active: boolean
  past: boolean
  isMobile: boolean
}

const MarginSides = styled.div<MarginSidesProps>`
  margin: 0 12px 0 ${(p) => `${LAYOUT_LEFT_EDGE - (p.active || p.past ? 6 : 1)}px`};

  @media (min-width: ${theme.maxWidths.paddedContent}) {
    margin-left: ${(p) => LAYOUT_LEFT_EDGE + (p.active || p.past ? 110 : 115)}px;
  }

  @media (min-width: ${theme.breakpoints.large}) {
    margin-left: ${(p) => LAYOUT_LEFT_EDGE + (p.active || p.past ? 130 : 135)}px;
  }
`

const Bold = styled.div`
  ${theme.text('normal', 'content', 'bold')};
  text-transform: uppercase;
  color: ${(p) => getColor(p.theme, ['grayDark'], ['grayPink'])};
`

const Weak = styled.div`
  ${theme.text('normal', 'content')};
  color: ${(p) => getColor(p.theme, ['grayDark'], ['grayPink'])};
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
`

const EquipmentWithFindings = styled.div`
  ${theme.text('normal')};
  color: ${(p) => getColor(p.theme, ['black'], ['grayPink'])};
`

const ErrorText = styled.div`
  ${theme.text('normal', 'content', 'bold')};
  color: ${(p) => getColor(p.theme, ['red'], ['red'])};
`

type Props = {
  t: TFunction
  open: boolean
  active: boolean
  past: boolean
  shiftId: string
  taskIndex: number
  hasInfo: boolean
  isMobile: boolean
  isCommuter: boolean
  contacts: Array<ContactProps>
  hasContacts: boolean
  router: Router
  loading: boolean
  taskEquipment: TaskEquipment
  locs: Array<string>
  commaSeparatedLocs: string
  shownEquipment: string
  taskTitle: string
  nightMode: boolean
  currentTaskId: number
  task: Task
  vehicleNumbers: Array<string>
  timetableParams: TimetableParams
  timetableError: string
  fromDetails: TaskDetailsInput
  toDetails: TaskDetailsInput
  even: boolean
  drawDelays: boolean
  delayObject: any
  setPunctualityParams: (trainDate: string, trainNumber: string) => void
  fetchTimetable: (isUsedForDriving: boolean, parts: Array<TimetableParams>) => void
  fetchFindings: (equipments: Array<string>) => void
  fetchTrainEquipmentCirculation: (params: TrainEquipmentCirculationParams) => void
  fetchWagons: (trainNumber: string, operatingDate: string) => void
  fetchContactLocations: (personnelNumbers: string) => void
  setPreselectedSerialNumbers: (serialNumbers: string[]) => void
  taskNameFull: string | null | undefined
  remarks: string | null | undefined
  shift: Shift
  operatingDate: string
  rollingGuides?: RollingGuideData
  equipments: dataWithStatus<Equipment[]>
  showTaskReport: boolean
  showFindings: boolean
  isLocationRelevantTask: boolean
  wagonEquipmentIds: Array<string>
  traction: string
  fallbackLocomotiveIdsUsed: boolean
}

type State = Partial<{
  showContacts: boolean
  reportOpen: boolean
  showConsumptionInfo: boolean
}>

class TaskRowInfo extends Component<Props, State> {
  fetchFindingsTimer: any
  fetchContactLocationsTimer: any
  static displayName: string

  constructor(props: any) {
    super(props)
    this.state = {
      showContacts: false,
      reportOpen: false,
      showConsumptionInfo: false,
    }
    this.toggleContacts = this.toggleContacts.bind(this)
    this.toggleReport = this.toggleReport.bind(this)
    this.handleCreateDefectEvent = this.handleCreateDefectEvent.bind(this)
    this.handleContactLocationsPolling = this.handleContactLocationsPolling.bind(this)
  }

  getFindingsEquipments(props: Props = this.props): string[] {
    return Array.from(new Set([...(props.locs || []), ...(props.wagonEquipmentIds || [])]))
  }

  startFindingsPolling(): void {
    clearInterval(this.fetchFindingsTimer)
    this.fetchFindingsTimer = setInterval(() => {
      const { fetchFindings } = this.props
      fetchFindings(this.getFindingsEquipments())
    }, FETCH_FINDINGS_POLL_RATE_MS)
  }

  shouldFetchWagons(props: Props = this.props): boolean {
    const { task, operatingDate, isCommuter } = props

    if (!task.trainNumberNumeric || !operatingDate || isCommuter) {
      return false
    }

    const inferredEquipments = inferTaskEquipment(task)

    if (!inferredEquipments.length) {
      return true
    }

    return inferredEquipments.some((equipment) => {
      return !isSm3(equipment) && !isSm6(equipment)
    })
  }

  fetchWagonsForTask(props: Props = this.props): void {
    const { fetchWagons, task, operatingDate } = props
    if (this.shouldFetchWagons(props)) {
      fetchWagons(task.trainNumberNumeric, operatingDate)
    }
  }

  fetchEquipmentCirculationForTask(props: Props = this.props): void {
    const { fetchTrainEquipmentCirculation, task, operatingDate, isCommuter } = props
    const { trainNumberNumeric, fromStation, toStation, taskStartDateTime, taskEndDateTime } = task

    if (trainNumberNumeric && operatingDate && !isCommuter) {
      fetchTrainEquipmentCirculation({
        trainNumber: trainNumberNumeric,
        fromStation: fromStation,
        toStation: toStation,
        startDateTime: taskStartDateTime,
        endDateTime: taskEndDateTime,
      })
    }
  }

  equipmentCirculationChanged(prevProps: Props): boolean {
    if (
      prevProps.task.trainNumberNumeric !== this.props.task.trainNumberNumeric ||
      prevProps.task.fromStation !== this.props.task.fromStation ||
      prevProps.task.toStation !== this.props.task.toStation ||
      prevProps.task.taskStartDateTime !== this.props.task.taskStartDateTime ||
      prevProps.task.taskEndDateTime !== this.props.task.taskEndDateTime ||
      prevProps.operatingDate !== this.props.operatingDate ||
      prevProps.isCommuter !== this.props.isCommuter
    ) {
      return true
    }
    return false
  }

  findingsEquipmentsChanged(prevProps: Props): boolean {
    const prevEquipments = this.getFindingsEquipments(prevProps)
    const currentEquipments = this.getFindingsEquipments(this.props)

    if (prevEquipments.length !== currentEquipments.length) {
      return true
    }

    const prevEquipmentSet = new Set(prevEquipments)
    return currentEquipments.some((equipment) => !prevEquipmentSet.has(equipment))
  }

  handleContactLocationsPolling(): void {
    const { isLocationRelevantTask, isCommuter, task, shift, fetchContactLocations } = this.props

    const shiftContacts = shift.contacts

    clearInterval(this.fetchContactLocationsTimer)
    if (isLocationRelevantTask && shiftContacts && isCommuter && task.trainNumberNumeric) {
      const personnelNumbers = shiftContacts
        .filter((contact) => contact.id)
        .map((contact) => contact.id)
        .toString()
      fetchContactLocations(personnelNumbers)

      this.fetchContactLocationsTimer = setInterval(() => {
        fetchContactLocations(personnelNumbers)
      }, FETCH_CONTACT_LOCATIONS_POLL_RATE_MS)
    }
  }

  componentDidMount() {
    const { fetchFindings } = this.props
    this.fetchWagonsForTask()
    fetchFindings(this.getFindingsEquipments())

    // Start timer for fetching updated findings
    this.startFindingsPolling()
    this.fetchEquipmentCirculationForTask()
    this.handleContactLocationsPolling()
  }

  componentDidUpdate(prevProps: Props) {
    const shouldFetchWagonsNow = this.shouldFetchWagons()
    const shouldFetchWagonsBefore = this.shouldFetchWagons(prevProps)

    if (
      this.props.task.trainNumberNumeric !== prevProps.task.trainNumberNumeric ||
      this.props.operatingDate !== prevProps.operatingDate ||
      (shouldFetchWagonsNow && !shouldFetchWagonsBefore)
    ) {
      this.fetchWagonsForTask()
    }

    if (this.equipmentCirculationChanged(prevProps)) {
      this.fetchEquipmentCirculationForTask()
    }

    if (this.findingsEquipmentsChanged(prevProps)) {
      this.props.fetchFindings(this.getFindingsEquipments())
      this.startFindingsPolling()
    }

    if (this.props.isLocationRelevantTask !== prevProps.isLocationRelevantTask) {
      this.handleContactLocationsPolling()
    }
  }

  componentWillUnmount() {
    clearInterval(this.fetchFindingsTimer)
    clearInterval(this.fetchContactLocationsTimer)
  }

  toggleContacts() {
    this.setState({
      showContacts: !this.state.showContacts,
    })
  }

  toggleReport() {
    this.setState({
      reportOpen: !this.state.reportOpen,
    })
  }

  toggleShowConsumptionInfo() {
    this.setState({
      showConsumptionInfo: !this.state.showConsumptionInfo,
    })
  }

  goToCreateDefectPage() {
    this.props.router.push('/create-defect')
  }

  handleCreateDefectEvent(eqs?: string[]) {
    const { setPreselectedSerialNumbers } = this.props

    if (eqs) setPreselectedSerialNumbers(eqs)
    this.goToCreateDefectPage()
  }

  render() {
    const {
      t,
      isCommuter,
      active,
      past,
      shiftId,
      taskIndex,
      hasInfo,
      isMobile,
      contacts,
      hasContacts,
      router,
      taskEquipment,
      locs,
      commaSeparatedLocs,
      taskTitle,
      nightMode,
      task,
      shift,
      vehicleNumbers,
      timetableParams,
      timetableError,
      fromDetails,
      toDetails,
      setPunctualityParams,
      fetchFindings,
      drawDelays,
      delayObject,
      taskNameFull,
      remarks,
      shownEquipment,
      operatingDate,
      open,
      rollingGuides,
      equipments,
      showTaskReport,
      showFindings,
      wagonEquipmentIds,
      traction,
      fallbackLocomotiveIdsUsed,
    }: Props = this.props

    const rollingGuideLink: string | undefined =
      rollingGuides?.[`${task.fromStation}-${task.toStation}`]

    const filterEquipments = (
      locs: string[],
      vehicleNumbers: string[],
      equipments?: Equipment[]
    ): string[] => [
      ...(locs?.filter((l) => equipments?.some((e) => e.installationGroup === l)) ?? []),
      ...(vehicleNumbers?.filter((v) => equipments?.some((e) => e.installationGroup === v)) ?? []),
    ]

    const filteredEquipments = filterEquipments(locs, vehicleNumbers, equipments.data)

    return (
      <div onClick={(e) => e.stopPropagation()}>
        {hasInfo ? (
          <MarginSides active={active} past={past} isMobile={isMobile}>
            {drawDelays && (
              <DelayLabelRow
                t={t}
                task={task}
                delays={delayObject.delays}
                delayMinutes={delayObject.delayMinutes}
                trainCategory={delayObject.trainCategory}
                isMobile={false} // show full labels in task view
                estimate={false}
                notDriving={false}
              />
            )}
            {taskTitle.length > TASK_TITLE_MAX_CHAR && (
              <Row>
                <Weak>{t('task')}</Weak>
                &nbsp;
                <Bold>{taskTitle}</Bold>
              </Row>
            )}
            {taskNameFull && (
              <Row>
                <Weak>{t('task')}</Weak>
                &nbsp;
                <Bold>{taskNameFull}</Bold>
                <Weak>, {t('train')}</Weak>
                &nbsp;
                <Bold>{`${task.trainCategory ? task.trainCategory : ''} ${
                  task.trainNumberNumeric
                }`}</Bold>
              </Row>
            )}
            <Row>
              <TaskDetails t={t} details={fromDetails} task={task} />
              <TaskDetails t={t} details={toDetails} task={task} />
            </Row>
            <EquipmentWithFindings>
              {remarks && <div>{remarks}</div>}
              <b>{traction || ''}</b>
              &nbsp;
              {shownEquipment}
            </EquipmentWithFindings>
            {taskEquipment.error && <ErrorText>{t('fetchingFindingsFailed')}</ErrorText>}
          </MarginSides>
        ) : undefined}
        {/* Button ordering:
          1. create defect
          2. findings
          3. compositions
          4. personnel
          5. timetable
        */}
        {open && (
          <MarginSides active={active} past={past} isMobile={isMobile}>
            {timetableError !== '' && <ErrorText>{t(timetableError)}</ErrorText>}
            {taskHasARestriction(taskEquipment) && (
              <RestrictionText color="error">{t('usageCode')}</RestrictionText>
            )}
            {this.state.reportOpen ? (
              <TaskReport
                trainNumber={task.trainNumberNumeric}
                toggleReport={this.toggleReport}
                task={task}
                shift={shift}
                nightMode={nightMode}
                t={t}
              />
            ) : (
              <TaskButtonRow
                fetchFindings={() => fetchFindings(this.getFindingsEquipments())}
                findingsError={taskEquipment.error || ''}
                findingsEnabled={
                  (locs.length > 0 || wagonEquipmentIds.length > 0) && !!task.trainNumberNumeric
                }
                compositionsEnabled={
                  !fallbackLocomotiveIdsUsed &&
                  !!task.fromStation &&
                  !!task.trainNumberNumeric &&
                  !isCommuter
                }
                punctualityEnabled={!!task.trainNumberNumeric}
                criticality={
                  taskEquipment.findings && taskEquipment.highestCriticality
                    ? '' + taskEquipment.highestCriticality.level
                    : null
                }
                loading={taskEquipment.loading}
                openCreateDefect={(eqs) => this.handleCreateDefectEvent(eqs)}
                filteredEquipments={filteredEquipments}
                openFindings={() => {
                  router.push(`/restrictionInfos/${commaSeparatedLocs}`)
                }}
                openCompositions={() => {
                  // compositionDate ensures that composition search is done for the right train when
                  // the train operatingDate is different from taskStartDate.
                  // task.operatingDate is used if taskStartDate is before the train operatingDate
                  // to ensure that composition data is not fetched for a previous day train.
                  // task.taskStartDateTime is used if taskStartDate is after or equal to the train
                  // operating date to ensure correct train data is fetched from KAPU.
                  // task.taskStartDateTime is also used when task.operatingDate is not defined.
                  const compositionDate =
                    task.operatingDate &&
                    moment(task.taskStartDateTime).isBefore(moment(task.operatingDate))
                      ? task.operatingDate
                      : task.taskStartDateTime

                  router.push(
                    `/composition/${moment(compositionDate).format('YYYY-MM-DDTHH:mm')}/${
                      task.fromStation
                    }/${task.trainNumberNumeric}`
                  )
                }}
                hasContacts={hasContacts}
                contactsOpen={hasContacts && this.state.showContacts}
                toggleContacts={this.toggleContacts}
                timetableParams={timetableParams}
                openPunctuality={
                  task.trainNumberNumeric
                    ? () => {
                        setPunctualityParams(operatingDate, task.trainNumberNumeric)
                        router.push(
                          `/punctuality/${moment(operatingDate).format('YYYY-MM-DD')}/${
                            task.trainNumberNumeric
                          }`
                        )
                        /* eslint-disable @typescript-eslint/no-empty-function */
                      }
                    : () => {}
                }
                nightMode={nightMode}
                toggleReport={this.toggleReport}
                isCommuter={isCommuter}
                showTaskReport={showTaskReport}
                showFindings={showFindings}
              />
            )}
          </MarginSides>
        )}

        {hasContacts && this.state.showContacts ? (
          <MarginSides active={active} past={past} isMobile={isMobile}>
            <ContactList contacts={contacts} shiftId={shiftId} taskIndex={taskIndex} />
          </MarginSides>
        ) : undefined}

        <MarginSides active={active} past={past} isMobile={isMobile}>
          <TaskTowingRowContainer router={router} locomotives={locs} />
        </MarginSides>

        {!!task.energyEfficiency?.shifts.length && (
          <MarginSides active={active} past={past} isMobile={isMobile}>
            <EnergyEfficiencyTaskInfo
              energyEfficiency={task.energyEfficiency}
              rollingGuideLink={rollingGuideLink}
            />
          </MarginSides>
        )}
      </div>
    )
  }
}

TaskRowInfo.displayName = 'TaskRowInfo'

type PropsIn = {
  open: boolean
  active: boolean
  past: boolean
  isMobile: boolean
  shift: Shift
  vehicleNumbers?: Array<string>
  router: Router
  locs: Array<string>
  commaSeparatedLocs: string
  taskTitle: string
  nightMode: boolean
  even: boolean
  drawDelays: boolean
  delayObject: any
  task: Task
  info: Array<{
    title: string
    value: string
  }>
  contacts: Array<TaskContactRef | Contact>
}

const mapStateToProps = (state: RootState, { shift, task, info, contacts }: PropsIn): any => {
  const now = nowSelector(state)
  const taskIndex = shift.tasks.findIndex(
    (shiftTask) => shiftTask.taskStartDateTime === task.taskStartDateTime
  )
  const taskWithNeighbors = shift.tasks.slice(
    Math.max(0, taskIndex - 1),
    Math.min(shift.tasks.length, taskIndex + 2)
  )
  const isLocationRelevantTask = taskWithNeighbors.some((neighborTask) =>
    taskIsActive(now, neighborTask)
  )

  const isCommuter = shift.isCommuter
  const hasInfo = info.length > 0
  const hasContacts = contacts.length > 0
  const findingsState = findingsSelector(state) as FindingsState
  const locomotiveEquipmentIds = inferTaskEquipment(task)
  const operatingDate = selectOperatingDateForTask(task)

  let locomotiveIds = locomotiveEquipmentIds
  let traction = task.traction || ''
  let trainEquipmentCirculation: TrainEquipmentCirculation | undefined = undefined
  let fallbackLocomotiveIdsUsed = false

  if (!isCommuter) {
    const equipmentInfoKey = generateEquipmnentInfoKeyFromTask(task)

    trainEquipmentCirculation = selectTrainEquipmentCirculationByKey(state, equipmentInfoKey)

    if (locomotiveIds.length === 0) {
      locomotiveIds = Array.from(
        new Set(trainEquipmentCirculation?.vehicleNumbers?.filter(Boolean) ?? [])
      )

      if (locomotiveIds.length) {
        fallbackLocomotiveIdsUsed = true
      }
    }

    if (!traction) {
      traction = Array.from(
        new Set(trainEquipmentCirculation?.vehicleTypes?.filter(Boolean) ?? [])
      ).join(', ')

      if (traction) {
        fallbackLocomotiveIdsUsed = true
      }
    }
  }

  const originalLocomotiveIds = task.locomotives?.map((loc) => loc.locId) ?? []

  // Skip wagons only when all known locomotives are SM3/SM6
  const allLocomotivesAreSm =
    originalLocomotiveIds.length > 0 && originalLocomotiveIds.every((id) => isSm3(id) || isSm6(id))

  const shouldIncludeWagonEquipmentIds = !isCommuter && !allLocomotivesAreSm

  const trainId = createTrainId(task.trainNumberNumeric || '', operatingDate)
  const trainParts = shouldIncludeWagonEquipmentIds
    ? state.wagonSection.fetchedTrains[trainId]?.data?.trainParts || []
    : []

  const wagonEquipmentIds = Array.from(
    new Set(
      trainParts.flatMap((part) =>
        part.vehicles
          .map((vehicle) => vehicle.assignedVehicle?.vehicleNumber)
          .filter((vehicleNumber): vehicleNumber is string => !!vehicleNumber)
      )
    )
  )

  const findingsEquipmentIds = shouldIncludeWagonEquipmentIds
    ? Array.from(new Set([...locomotiveIds, ...wagonEquipmentIds]))
    : locomotiveIds

  const taskEquipment = selectEquipmentFindings(findingsState, findingsEquipmentIds)
  const highestCriticality = findMostCritical(taskEquipment.findings, {
    ignoreServiceLimitations: state.user.driver || state.user.logistics_driver,
  })
  const timetableParams = shiftTasksToTimetableParams(shift).find(
    (p) =>
      p.trainNumber === task.trainNumber &&
      moment(task.taskStartDateTime).isBetween(
        moment(p.depTime),
        moment(p.arrTime),
        'minute',
        '[]'
      ) &&
      moment(task.taskEndDateTime).isBetween(moment(p.depTime), moment(p.arrTime), 'minute', '[]')
  )

  const timetableError = state.timetable.error

  // TODO clean up the info as this is the only place it is actually used
  const pickupLocation = info.find((i) => i.title === 'Ottosijainti')
  const dropOffLocation = info.find((i) => i.title === 'Jättösijainti')

  // TODO work out if we can show the remarks better at some point
  const remarks = task.locomotives
    ? task.locomotives
        .filter((loc) => !!loc.remarks && loc.remarks !== '')
        .map((loc) => `${loc.locId}: ${loc.remarks || ''}`)
        .join('\n')
    : null

  let shownEquipment = inferTaskEquipmentDisplay(task).join(',')

  if (!shownEquipment && trainEquipmentCirculation) {
    shownEquipment = Array.from(
      new Set(trainEquipmentCirculation?.vehicleNumbers?.filter(Boolean) ?? [])
    ).join(',')
  }

  const commaSeparatedEquipmentIds = filterEquipments(findingsEquipmentIds).join(',')
  const fromDetails = isCommuter
    ? commuterFromTrainDetails(task)
    : {
        type: 'from',
        station: getName(task.fromStation),
        trains: task.previousArrival
          ? [
              {
                trainNumber: task.previousArrival,
                time: null,
              },
            ]
          : [],
        location: (pickupLocation && pickupLocation.value) || '',
        lockingInformation: task.lockingInformation,
      }

  const toDetails = isCommuter
    ? commuterToTrainDetails(task)
    : {
        type: 'to',
        station: getName(task.toStation),
        trains: task.nextDeparture
          ? [
              {
                trainNumber: task.nextDeparture,
                time: null,
              },
            ]
          : [],
        location: (dropOffLocation && dropOffLocation.value) || '',
      }

  const taskContacts: Array<Partial<TaskContact>> = resolveTaskContactReferences(state)(contacts)

  const taskNameFull =
    state.user.personnelGroup === 'M' &&
    (task.taskName === 'kond' || task.taskName === 'akond' || task.taskName === 'akondp') &&
    task.taskName
  //How many characters the title can approximately fit, longer titles need to be shown in the task description

  const rollingGuides = state.rollingGuides.rollingGuides

  const showTaskReport = isCommuter && !state.user.commuter_conductor
  const showFindings = !isCommuter || !state.user.commuter_conductor

  return {
    isCommuter,
    hasInfo,
    shiftId: shift.id,
    taskIndex: task.id,
    locs: locomotiveIds,
    contacts: isCommuter ? taskContacts : contacts,
    hasContacts,
    taskEquipment: {
      ...taskEquipment,
      highestCriticality: highestCriticality,
    },
    commaSeparatedLocs: commaSeparatedEquipmentIds,
    shownEquipment,
    timetableParams,
    timetableError,
    fromDetails,
    toDetails,
    taskNameFull,
    remarks,
    operatingDate,
    rollingGuides,
    equipments: state.defectCommonData.equipments,
    showTaskReport,
    showFindings,
    isLocationRelevantTask,
    wagonEquipmentIds,
    traction,
    fallbackLocomotiveIdsUsed,
  }
}

const mapDispatchToProps = (dispatch: TypedDispatch) => ({
  fetchTimetable: (isUsedForDriving: boolean, parts: TimetableParams[]) =>
    dispatch(fetchTimetable(isUsedForDriving, parts)),
  setPunctualityParams: (trainDate: string, trainNumber: string) =>
    dispatch(setPunctualityParams({ trainDate: trainDate, trainNumber: trainNumber })),
  fetchFindings: (equipments: string[]) => dispatch(fetchFindings(filterEquipments(equipments))),
  fetchTrainEquipmentCirculation: (params: TrainEquipmentCirculationParams) =>
    dispatch(fetchTrainEquipmentCirculationAction(params)),
  fetchWagons: (trainNumber: string, operatingDate: string) =>
    dispatch(fetchTrainDataAction({ trainNumber, businessDate: operatingDate })),
  setPreselectedSerialNumbers: (serialNumbers: string[]) =>
    dispatch(setPreSelectedSerialNumbers(serialNumbers)),
  fetchContactLocations: (personnelNumbers: string) =>
    dispatch(fetchContactLocations(personnelNumbers)),
})

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(TaskRowInfo) as unknown as ComponentType<PropsIn>
)
